/**
 * Sieve — spoiler-safe share strings (§7).
 *
 * CRITICAL CONSTRAINT: the share string must never reveal WHERE the primes are.
 * A positional 5×5 emoji grid would leak the answer. So we encode the TAP
 * SEQUENCE — 🟩 for a correct tap, 🟥 for a mistake, in the order they happened
 * — which is at most 8 glyphs (5 correct + 3 mistakes) and layout-free.
 */

import { BRAND, SHARE_DOMAIN } from './constants';
import type { GameState, TapLog } from './types';

const GREEN = '🟩';
const RED = '🟥';

/** The glyph line, e.g. "🟩🟩🟥🟩🟩🟩". */
export function glyphLine(tapResults: TapLog): string {
  return tapResults.map((r) => (r === 'correct' ? GREEN : RED)).join('');
}

interface ShareData {
  puzzleId: number;
  found: number;
  mistakes: number;
  tapResults: TapLog;
  currentStreak: number;
  /** Multiples mode only — adds "▪ ÷7" and a "Multiples" label (Multiples §7). */
  divisor?: number;
}

/**
 * Builds the full multi-line share text (§7). Prime:
 *
 *   Sieve #142 ▪ 5/5, 1 mistake
 *   🟩🟩🟥🟩🟩🟩
 *   Streak: 12
 *   sievedaily.com
 *
 * Multiples adds its label and the (public) divisor:
 *
 *   Sieve Multiples #12 ▪ ÷7 ▪ 5/5, 1 mistake
 */
export function buildShareText(d: ShareData): string {
  const mistakeTail = d.mistakes === 0 ? 'flawless' : d.mistakes === 1 ? '1 mistake' : `${d.mistakes} mistakes`;
  const title = d.divisor ? `${BRAND} Multiples #${d.puzzleId} ▪ ÷${d.divisor}` : `${BRAND} #${d.puzzleId}`;
  const scoreLine = `${title} ▪ ${d.found}/5, ${mistakeTail}`;

  const lines = [scoreLine, glyphLine(d.tapResults)];
  if (d.currentStreak > 0) lines.push(`Streak: ${d.currentStreak}`);
  lines.push(SHARE_DOMAIN);
  return lines.join('\n');
}

export function shareFromGame(g: GameState, currentStreak: number): string {
  return buildShareText({
    puzzleId: g.puzzle.id,
    found: g.found,
    mistakes: g.mistakes,
    tapResults: g.tapResults,
    currentStreak,
    divisor: g.puzzle.divisor,
  });
}

/** Rapid-mode share line (§10): `Sieve Rapid ▪ streak 34`. */
export function buildRapidShareText(streak: number): string {
  return `${BRAND} Rapid ▪ streak ${streak}\n${SHARE_DOMAIN}`;
}

/**
 * Copies text to the clipboard and/or opens the native share sheet (§7).
 * Returns how it was shared so the UI can show the right confirmation.
 */
export async function shareOrCopy(
  text: string,
  title = BRAND,
): Promise<'shared' | 'copied' | 'failed'> {
  const nav = navigator as Navigator & {
    share?: (data: ShareData_) => Promise<void>;
  };
  type ShareData_ = { title?: string; text?: string };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title, text });
      return 'shared';
    } catch {
      // User dismissed the sheet or it failed — fall through to clipboard.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
